import { Loader2, Music, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useUploadMusicMutation } from '~entities/recital-manager/api/upload-music';
import type { Performance } from '~entities/recital-manager/model/recital';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';

interface UploadMusicFileProps {
  actId: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  performance: Performance | null;
  setSelectedPerformance?: React.Dispatch<
    React.SetStateAction<Performance | null>
  >;
}

export const UploadMusicFile = ({
  actId,
  open,
  setOpen,
  performance,
  setSelectedPerformance,
}: UploadMusicFileProps) => {
  const [musicFile, setMusicFile] = useState<File | null>(null);

  const { mutateAsync: uploadMusic, isPending: isUploadingMusic } =
    useUploadMusicMutation();

  const handleUploadMusic = async () => {
    if (!performance || !musicFile) return;
    try {
      await uploadMusic({
        actId,
        performanceId: performance._id,
        file: musicFile,
      });
      toast.success('Music uploaded successfully!');
      setOpen(false);
      setMusicFile(null);
      if (setSelectedPerformance) {
        setSelectedPerformance(null);
      }
    } catch (error) {
      console.error('Error uploading music file:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Music</DialogTitle>
          <DialogDescription>
            Upload music file for &quot;{performance?.name}&quot;
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <label className='text-sm font-medium'>Music File</label>
            <div className='flex items-center gap-2'>
              <Input
                type='file'
                accept='audio/mpeg,audio/wav,audio/mp4,audio/x-m4a'
                onChange={(e) => {
                  setMusicFile(e.target.files?.[0] ?? null);
                }}
                className='cursor-pointer'
              />
            </div>
            {musicFile && (
              <div className='flex items-center gap-2 rounded border p-2 text-sm'>
                <Music className='size-4' />
                <span className='flex-1'>{musicFile.name}</span>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => {
                    setMusicFile(null);
                  }}
                >
                  <X className='size-4' />
                </Button>
              </div>
            )}
            <p className='text-xs text-muted-foreground'>
              Accepted formats: MP3, WAV, MP4, M4A (Max 20MB)
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => {
              setOpen(false);
              setMusicFile(null);
              if (setSelectedPerformance) {
                setSelectedPerformance(performance);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadMusic}
            disabled={!musicFile || isUploadingMusic}
          >
            {isUploadingMusic && (
              <Loader2 className='mr-2 size-4 animate-spin' />
            )}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
